import React, { useEffect, useMemo, useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { supabase } from '../../../../services/supabaseClient';
import { formatPrice, buildPropertySlug, aprovarImovel, rejeitarImovel } from '../../services/propertyService';
import { MF_BASE } from '../../basePath';
import BiancaDashboard from './BiancaDashboard';
import AmenidadesPanelV2 from './AmenidadesPanelV2';
import CorretoresPanelV2 from './CorretoresPanelV2';
import VinculosPanelV2 from './VinculosPanelV2';
import EmailBlastPanel from './EmailBlastPanel';

// ============ Themes ============
const lightTheme = {
  mode: 'light',
  bg: '#FAFAF7',
  surface: '#FFFFFF',
  surfaceAlt: '#F5F3EE',
  ink: '#1A1F2B',
  inkSoft: '#3D434F',
  muted: '#6B7280',
  line: '#E8E5DE',
  lineDashed: '#E8E5DE',
  gold: '#B8935A',
  goldSoft: '#F2EBDD',
  ok: '#2F7D5B',
  okSoftBg: 'rgba(47,125,91,.06)',
  shadow: 'rgba(0,0,0,.06)',
};

const darkTheme = {
  mode: 'dark',
  bg: '#0F1117',
  surface: '#181B23',
  surfaceAlt: '#1F2330',
  ink: '#F1ECE2',
  inkSoft: '#C9C4B8',
  muted: '#8A8F9A',
  line: '#262A36',
  lineDashed: '#262A36',
  gold: '#C9A06A',
  goldSoft: 'rgba(201,160,106,.14)',
  ok: '#5BB089',
  okSoftBg: 'rgba(91,176,137,.10)',
  shadow: 'rgba(0,0,0,.4)',
};

const THEME_KEY = 'mf_admin_v2_theme';
const VIDEO_RE = /\.(mp4|mov|webm|avi|mkv|m4v)(\?|$)/i;
const pickCover = (imgs) => {
  if (!Array.isArray(imgs)) return null;
  return imgs.find(u => u && !VIDEO_RE.test(u)) || null;
};

// ============ Icons ============
const Icon = ({ d, size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);
const IconArea = (p) => <Icon size={p.size} d={['M3 3h7v7H3z', 'M14 14h7v7h-7z', 'M3 21h7M21 3v7']} />;
const IconBed = (p) => <Icon size={p.size} d={['M3 18v-7a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v7', 'M3 14h18', 'M3 21v-3M21 21v-3', 'M7 11V8.5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1V11', 'M13 11V8.5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1V11']} />;
const IconShower = (p) => <Icon size={p.size} d={['M6 12h14v2a6 6 0 0 1-12 0v-2H6z', 'M9 4v3', 'M9 4a3 3 0 0 1 3 3h-6a3 3 0 0 1 3-3z', 'M10 19v2M14 19v2M12 19v3']} />;
const IconCar = (p) => <Icon size={p.size} d={['M5 17h14', 'M5 17l1.5-5h11L19 17', 'M5 17v3a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-3', 'M16 17v3a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-3', 'M8 14h8']} />;

// ============ Styled ============
const Shell = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  min-height: 100vh;
  background: ${p => p.theme.bg};
  color: ${p => p.theme.ink};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  transition: background .25s ease, color .25s ease;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const Sidebar = styled.aside`
  background: ${p => p.theme.surface};
  border-right: 1px solid ${p => p.theme.line};
  padding: 32px 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  @media (max-width: 900px) {
    border-right: none;
    border-bottom: 1px solid ${p => p.theme.line};
    flex-direction: row;
    flex-wrap: wrap;
    padding: 14px 20px;
    gap: 6px;
    align-items: center;
  }
`;

const Brand = styled.div`
  padding: 0 8px 24px;
  border-bottom: 1px solid ${p => p.theme.line};
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  img {
    height: 56px;
    width: auto;
    max-width: 180px;
    object-fit: contain;
    display: block;
  }
  span.tagline {
    font-family: 'Inter', sans-serif;
    font-size: 10px;
    letter-spacing: 3px;
    color: ${p => p.theme.gold};
    text-transform: uppercase;
    font-weight: 500;
  }
  @media (max-width: 900px) {
    border-bottom: none;
    padding: 0 8px 0 0;
    margin: 0 auto 0 0;
    flex-direction: row;
    gap: 12px;
    img { height: 36px; }
    span.tagline { display: none; }
  }
`;

const NavGroup = styled.div`
  font-size: 10px;
  letter-spacing: 2.5px;
  color: ${p => p.theme.muted};
  text-transform: uppercase;
  padding: 16px 8px 8px;
  font-weight: 500;
  @media (max-width: 900px) { display: none; }
`;

const NavItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  border: none;
  background: ${p => p.$active ? p.theme.goldSoft : 'transparent'};
  color: ${p => p.$active ? p.theme.ink : p.theme.inkSoft};
  padding: 11px 14px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: ${p => p.$active ? 600 : 400};
  cursor: pointer;
  text-align: left;
  letter-spacing: 0.2px;
  transition: background .15s;
  &:hover { background: ${p => p.$active ? p.theme.goldSoft : p.theme.surfaceAlt}; }
  &:disabled { color: ${p => p.theme.muted}; cursor: not-allowed; opacity: .6; }
  small {
    margin-left: auto;
    font-size: 9px;
    letter-spacing: 1.5px;
    color: ${p => p.theme.gold};
    text-transform: uppercase;
  }
`;

const Main = styled.main`
  display: flex;
  flex-direction: column;
`;

const Topbar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 40px;
  border-bottom: 1px solid ${p => p.theme.line};
  background: ${p => p.theme.surface};
  flex-wrap: wrap;
  gap: 12px;
  @media (max-width: 600px) { padding: 14px 20px; }
`;

const Content = styled.div`
  padding: 40px 48px;
  width: 100%;
  @media (max-width: 1100px) { padding: 32px 28px; }
  @media (max-width: 600px) { padding: 24px 16px; }
`;

const Crumb = styled.div`
  font-size: 12px;
  letter-spacing: 1.5px;
  color: ${p => p.theme.muted};
  text-transform: uppercase;
  span { color: ${p => p.theme.ink}; font-weight: 500; }
`;

const TopActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const TopBtn = styled.button`
  border: 1px solid ${p => p.theme.line};
  background: ${p => p.$primary ? p.theme.ink : p.theme.surface};
  color: ${p => p.$primary ? p.theme.bg : p.theme.ink};
  padding: 9px 18px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: all .15s;
  &:hover { transform: translateY(-1px); box-shadow: 0 2px 8px ${p => p.theme.shadow}; }
`;

const ThemeToggle = styled.button`
  border: 1px solid ${p => p.theme.line};
  background: ${p => p.theme.surface};
  color: ${p => p.theme.ink};
  width: 38px;
  height: 38px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: all .2s;
  &:hover { background: ${p => p.theme.surfaceAlt}; transform: rotate(15deg); }
`;

const Greeting = styled.div`
  margin-bottom: 36px;
  h1 {
    font-family: 'Cormorant Garamond', 'Playfair Display', Georgia, serif;
    font-size: 38px;
    font-weight: 500;
    color: ${p => p.theme.ink};
    margin: 0 0 6px;
    letter-spacing: -0.3px;
  }
  p {
    color: ${p => p.theme.muted};
    font-size: 14px;
    margin: 0;
  }
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
  margin-bottom: 36px;
  @media (max-width: 980px) { grid-template-columns: repeat(2, 1fr); }
`;

const KpiCard = styled.div`
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.line};
  border-radius: 10px;
  padding: 22px 24px;
  position: relative;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: ${p => p.theme.gold};
    opacity: .6;
  }
`;

const KpiLabel = styled.div`
  font-size: 10px;
  letter-spacing: 2.5px;
  color: ${p => p.theme.muted};
  text-transform: uppercase;
  font-weight: 500;
`;

const KpiValue = styled.div`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 42px;
  font-weight: 500;
  color: ${p => p.theme.ink};
  margin-top: 8px;
  line-height: 1;
`;

const KpiHint = styled.div`
  font-size: 12px;
  color: ${p => p.theme.muted};
  margin-top: 8px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  @media (max-width: 980px) { grid-template-columns: 1fr; }
`;

const Panel = styled.section`
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.line};
  border-radius: 10px;
  padding: 24px 26px;
`;

const PanelHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 18px;
  h2 {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 22px;
    font-weight: 600;
    margin: 0;
    color: ${p => p.theme.ink};
  }
  a {
    font-size: 12px;
    letter-spacing: 1px;
    color: ${p => p.theme.gold};
    text-transform: uppercase;
    text-decoration: none;
    cursor: pointer;
    &:hover { text-decoration: underline; }
  }
`;

const PropList = styled.div`
  display: flex;
  flex-direction: column;
`;

const PropRow = styled.div`
  display: grid;
  grid-template-columns: 64px 1fr auto;
  gap: 16px;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid ${p => p.theme.line};
  &:last-child { border-bottom: none; }
`;

const Thumb = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 8px;
  background: ${p => p.theme.surfaceAlt};
  border: 1px solid ${p => p.theme.line};
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
`;

const PropTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${p => p.theme.ink};
  line-height: 1.35;
`;

const PropMeta = styled.div`
  font-size: 12px;
  color: ${p => p.theme.muted};
  margin-top: 3px;
  letter-spacing: 0.2px;
`;

const Price = styled.div`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 20px;
  font-weight: 600;
  color: ${p => p.theme.ink};
  text-align: right;
`;

const StatusPill = styled.span`
  display: inline-block;
  font-size: 10px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  padding: 3px 9px;
  border-radius: 999px;
  border: 1px solid ${p => p.$on ? p.theme.ok : p.theme.line};
  color: ${p => p.$on ? p.theme.ok : p.theme.muted};
  background: ${p => p.$on ? p.theme.okSoftBg : 'transparent'};
  margin-top: 6px;
`;

const SidePanel = styled(Panel)`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Stat = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 8px 0;
  border-bottom: 1px dashed ${p => p.theme.lineDashed};
  &:last-child { border-bottom: none; }
  span:first-child {
    font-size: 13px;
    color: ${p => p.theme.muted};
  }
  span:last-child {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 22px;
    font-weight: 600;
    color: ${p => p.theme.ink};
  }
`;

const Empty = styled.div`
  padding: 40px 0;
  text-align: center;
  color: ${p => p.theme.muted};
  font-size: 13px;
`;

// ============ List view styles ============
const PageHead = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 28px;
  flex-wrap: wrap;
  h1 {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 36px;
    font-weight: 500;
    margin: 0 0 4px;
    color: ${p => p.theme.ink};
  }
  p {
    color: ${p => p.theme.muted};
    font-size: 13px;
    margin: 0;
  }
`;

const Filters = styled.div`
  display: grid;
  grid-template-columns: minmax(220px, 2.2fr) repeat(4, minmax(120px, 1fr)) 100px 100px auto;
  gap: 12px;
  align-items: end;
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.line};
  border-radius: 10px;
  padding: 16px 18px;
  margin-bottom: 22px;
  @media (max-width: 1280px) {
    grid-template-columns: repeat(4, minmax(140px, 1fr)) auto;
    & > label:first-child { grid-column: span 2; }
  }
  @media (max-width: 720px) {
    grid-template-columns: 1fr 1fr;
    & > label:first-child { grid-column: span 2; }
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 10px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: ${p => p.theme.muted};
  font-weight: 500;
  input, select {
    background: ${p => p.theme.bg};
    border: 1px solid ${p => p.theme.line};
    color: ${p => p.theme.ink};
    border-radius: 6px;
    padding: 8px 10px;
    font-size: 13px;
    font-family: inherit;
    letter-spacing: 0;
    text-transform: none;
    font-weight: 400;
    outline: none;
    &:focus { border-color: ${p => p.theme.gold}; }
  }
`;

const ClearBtn = styled.button`
  border: 1px solid ${p => p.theme.line};
  background: transparent;
  color: ${p => p.theme.muted};
  padding: 8px 14px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  letter-spacing: 0.5px;
  height: 35px;
  align-self: end;
  &:hover { color: ${p => p.theme.ink}; border-color: ${p => p.theme.gold}; }
`;

const ResultBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
  font-size: 12px;
  letter-spacing: 1px;
  color: ${p => p.theme.muted};
  text-transform: uppercase;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
`;

const Card = styled.article`
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.line};
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform .2s, box-shadow .2s;
  &:hover { transform: translateY(-3px); box-shadow: 0 8px 24px ${p => p.theme.shadow}; }
`;

const CardCover = styled.div`
  aspect-ratio: 4/3;
  background: ${p => p.theme.surfaceAlt};
  position: relative;
  border-bottom: 1px solid ${p => p.theme.line};
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .ph {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${p => p.theme.muted};
    font-size: 11px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
  }
`;

const CoverBadges = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const Badge = styled.span`
  font-size: 9px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  background: ${p => p.$gold ? p.theme.gold : 'rgba(0,0,0,.65)'};
  color: ${p => p.$gold ? '#fff' : '#fff'};
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 500;
  backdrop-filter: blur(6px);
`;

const AprovBadge = styled.span`
  font-size: 9px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
  background: ${p =>
    p.$status === 'pendente' ? 'rgba(212, 165, 106, .95)' :
    p.$status === 'rejeitado' ? 'rgba(220, 90, 90, .95)' :
    'transparent'};
  color: #fff;
`;

const CardBody = styled.div`
  padding: 16px 18px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 19px;
  font-weight: 600;
  color: ${p => p.theme.ink};
  line-height: 1.25;
  transition: color .15s;
  &:hover { color: ${p => p.theme.gold}; }
`;

const CardLoc = styled.div`
  font-size: 12px;
  color: ${p => p.theme.muted};
  letter-spacing: 0.2px;
`;

const CardSpecs = styled.div`
  font-size: 12px;
  color: ${p => p.theme.inkSoft};
  display: flex;
  justify-content: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 8px;
  padding: 8px 0;
  border-top: 1px solid ${p => p.theme.line};
  border-bottom: 1px solid ${p => p.theme.line};
  span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    svg { color: ${p => p.theme.gold}; flex-shrink: 0; }
  }
`;

const CardPrice = styled.div`
  margin-top: auto;
  padding-top: 10px;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 22px;
  font-weight: 600;
  color: ${p => p.theme.ink};
`;

const CardActions = styled.div`
  display: flex;
  border-top: 1px solid ${p => p.theme.line};
  button {
    flex: 1;
    min-width: 0;
    border: none;
    background: transparent;
    color: ${p => p.theme.inkSoft};
    padding: 10px 6px;
    font-size: 12px;
    letter-spacing: 0.2px;
    cursor: pointer;
    font-family: inherit;
    font-weight: 500;
    border-right: 1px solid ${p => p.theme.line};
    transition: background .15s, color .15s;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    &:last-child { border-right: none; }
    &:hover { background: ${p => p.theme.surfaceAlt}; color: ${p => p.theme.gold}; }
    &[data-danger="true"]:hover { color: #C5544A; }
  }
`;

const CoverActions = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  gap: 6px;
`;
const CircleBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: ${p => p.$on ? p.theme.gold : 'rgba(0,0,0,.55)'};
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(6px);
  transition: transform .15s, background .15s;
  text-decoration: none;
  &:hover { transform: scale(1.08); background: ${p => p.$on ? p.theme.gold : 'rgba(0,0,0,.75)'}; }
`;

const ViewToggle = styled.div`
  display: inline-flex;
  border: 1px solid ${p => p.theme.line};
  border-radius: 6px;
  overflow: hidden;
  background: ${p => p.theme.surface};
`;

const ViewToggleBtn = styled.button`
  border: none;
  background: ${p => p.$active ? p.theme.ink : 'transparent'};
  color: ${p => p.$active ? p.theme.bg : p.theme.inkSoft};
  padding: 7px 14px;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.3px;
  cursor: pointer;
  font-family: inherit;
  transition: all .15s;
  &:hover:not([data-active="true"]) { color: ${p => p.theme.ink}; }
`;

const ListWrap = styled.div`
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.line};
  border-radius: 10px;
  overflow: hidden;
`;

const ListRow = styled.div`
  display: grid;
  grid-template-columns: 88px 1fr auto auto;
  gap: 18px;
  align-items: center;
  padding: 12px 18px;
  border-bottom: 1px solid ${p => p.theme.line};
  &:last-child { border-bottom: none; }
  &:hover { background: ${p => p.theme.surfaceAlt}; }
  @media (max-width: 720px) {
    grid-template-columns: 64px 1fr;
    & > :nth-child(3), & > :nth-child(4) { grid-column: span 2; justify-self: end; }
  }
`;

const ListThumb = styled.div`
  width: 88px;
  height: 64px;
  border-radius: 6px;
  background: ${p => p.theme.surfaceAlt};
  border: 1px solid ${p => p.theme.line};
  position: relative;
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
  ${p => p.$star && `
    &::after {
      content: '★';
      position: absolute;
      top: 4px; right: 4px;
      width: 18px; height: 18px;
      border-radius: 50%;
      background: ${p.theme.gold};
      color: #fff;
      font-size: 10px;
      display: flex; align-items: center; justify-content: center;
    }
  `}
`;

const ListInfo = styled.div`
  min-width: 0;
  .t { font-size: 14px; font-weight: 500; color: ${p => p.theme.ink}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; transition: color .15s; }
  .t:hover { color: ${p => p.theme.gold}; }
  .m { font-size: 12px; color: ${p => p.theme.muted}; margin-top: 2px; }
  .s { font-size: 11px; color: ${p => p.theme.inkSoft}; margin-top: 4px; display: flex; gap: 12px; flex-wrap: wrap; }
  .s span { display: inline-flex; align-items: center; gap: 4px; }
  .s svg { color: ${p => p.theme.gold}; }
`;

const ListPrice = styled.div`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 19px;
  font-weight: 600;
  color: ${p => p.theme.ink};
  white-space: nowrap;
`;

const ListActions = styled.div`
  display: flex;
  gap: 6px;
  button {
    border: 1px solid ${p => p.theme.line};
    background: transparent;
    color: ${p => p.theme.inkSoft};
    padding: 6px 10px;
    font-size: 11px;
    border-radius: 5px;
    cursor: pointer;
    font-family: inherit;
    transition: all .15s;
    &:hover { color: ${p => p.theme.gold}; border-color: ${p => p.theme.gold}; }
    &[data-danger="true"]:hover { color: #C5544A; border-color: #C5544A; }
  }
`;

const Pager = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  margin-top: 32px;
  button {
    min-width: 36px;
    height: 36px;
    padding: 0 10px;
    border: 1px solid ${p => p.theme.line};
    background: ${p => p.theme.surface};
    color: ${p => p.theme.ink};
    border-radius: 6px;
    font-size: 13px;
    cursor: pointer;
    font-family: inherit;
    transition: all .15s;
    &:hover:not(:disabled) { border-color: ${p => p.theme.gold}; color: ${p => p.theme.gold}; }
    &:disabled { opacity: .4; cursor: not-allowed; }
  }
  button[data-active="true"] {
    background: ${p => p.theme.ink};
    color: ${p => p.theme.bg};
    border-color: ${p => p.theme.ink};
  }
`;

// ============ Component ============
const PAGE_SIZE = 12;
const NAV_KEY = 'mf_admin_v2_nav';

export default function AdminPageV2({
  admin,
  onBackToClassic, onLogout,
  onNew, onEdit, onConfig, onBlog,
  onToggleStatus, onToggleDestaque, onDelete,
}) {
  const isDev = !!admin?.is_dev;
  const canSeeDisparos = isDev || admin?.role === 'admin';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nav, setNav] = useState(() => {
    if (typeof window === 'undefined') return 'dashboard';
    return localStorage.getItem(NAV_KEY) || 'dashboard';
  });
  const setNavSaved = (v) => {
    setNav(v);
    if (typeof window !== 'undefined') localStorage.setItem(NAV_KEY, v);
  };
  const [filters, setFilters] = useState({
    texto: '', status: '', tipo: '', bairro: '', cidade: '', precoMin: '', precoMax: '',
    aprovacao: '',
  });
  const [page, setPage] = useState(1);
  const [display, setDisplay] = useState(() => {
    if (typeof window === 'undefined') return 'cards';
    return localStorage.getItem('mf_admin_v2_display') === 'list' ? 'list' : 'cards';
  });
  const setDisplaySaved = (v) => {
    setDisplay(v);
    if (typeof window !== 'undefined') localStorage.setItem('mf_admin_v2_display', v);
  };
  const setFilter = (k) => (e) => { setFilters(f => ({ ...f, [k]: e.target.value })); setPage(1); };
  const clearFilters = () => {
    setFilters({ texto: '', status: '', tipo: '', bairro: '', cidade: '', precoMin: '', precoMax: '', aprovacao: '' });
    setPage(1);
  };
  const [mode, setMode] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    return localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light';
  });

  const theme = mode === 'dark' ? darkTheme : lightTheme;

  const toggleMode = () => {
    const next = mode === 'dark' ? 'light' : 'dark';
    setMode(next);
    localStorage.setItem(THEME_KEY, next);
  };

  const fetchItems = async () => {
    const { data } = await supabase
      .from('mf_imoveis')
      .select('*')
      .order('created_at', { ascending: false });
    setItems(data || []);
    setLoading(false);
  };

  useEffect(() => { fetchItems(); }, []);

  const handleToggleStatus = async (item) => {
    if (onToggleStatus) await onToggleStatus(item);
    fetchItems();
  };
  const handleToggleDestaque = async (item) => {
    if (onToggleDestaque) await onToggleDestaque(item);
    fetchItems();
  };
  const handleDelete = async (id) => {
    if (onDelete) await onDelete(id);
    fetchItems();
  };

  const handleAprovar = async (item) => {
    if (!window.confirm(`Aprovar "${item.titulo || 'este imóvel'}"? Ele ficará visível no site assim que estiver publicado.`)) return;
    const { data: { user } } = await supabase.auth.getUser();
    const adminEmail = user?.email || null;
    const res = await aprovarImovel(item.id, adminEmail);
    if (!res.ok) { alert('Erro ao aprovar: ' + res.error); return; }
    fetchItems();
  };
  const handleRejeitar = async (item) => {
    const motivo = window.prompt(`Por que rejeitar "${item.titulo || 'este imóvel'}"? O corretor verá esse motivo no painel.`);
    if (motivo === null) return; // cancelou
    const { data: { user } } = await supabase.auth.getUser();
    const adminEmail = user?.email || null;
    const res = await rejeitarImovel(item.id, motivo, adminEmail);
    if (!res.ok) { alert('Erro ao rejeitar: ' + res.error); return; }
    fetchItems();
  };

  const filtered = useMemo(() => {
    const q = filters.texto.trim().toLowerCase();
    const min = filters.precoMin ? Number(filters.precoMin) : null;
    const max = filters.precoMax ? Number(filters.precoMax) : null;
    return items.filter(i => {
      if (filters.status && i.status !== filters.status) return false;
      if (filters.aprovacao && (i.status_aprovacao || 'aprovado') !== filters.aprovacao) return false;
      if (filters.tipo && i.tipo !== filters.tipo) return false;
      if (filters.bairro && i.bairro !== filters.bairro) return false;
      if (filters.cidade && i.cidade !== filters.cidade) return false;
      if (min !== null && (Number(i.preco) || 0) < min) return false;
      if (max !== null && (Number(i.preco) || 0) > max) return false;
      if (q) {
        const blob = `${i.titulo || ''} ${i.endereco || ''} ${i.condominio || ''} ${i.bairro || ''} ${i.cidade || ''}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [items, filters]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const bairrosUnicos = useMemo(
    () => Array.from(new Set(items.map(i => i.bairro).filter(Boolean))).sort(),
    [items]
  );
  const cidadesUnicas = useMemo(
    () => Array.from(new Set(items.map(i => i.cidade).filter(Boolean))).sort(),
    [items]
  );

  const stats = useMemo(() => {
    const total = items.length;
    const publicados = items.filter(i => i.status === 'publicado').length;
    const rascunhos = items.filter(i => i.status === 'rascunho').length;
    const destaques = items.filter(i => i.destaque).length;
    const valorMedio = (() => {
      const prices = items.map(i => Number(i.preco)).filter(n => n > 0);
      if (!prices.length) return 0;
      return prices.reduce((a, b) => a + b, 0) / prices.length;
    })();
    const tipos = items.reduce((acc, i) => {
      const t = i.tipo || 'outro';
      acc[t] = (acc[t] || 0) + 1;
      return acc;
    }, {});
    const bairros = items.reduce((acc, i) => {
      const b = i.bairro || '—';
      acc[b] = (acc[b] || 0) + 1;
      return acc;
    }, {});
    return { total, publicados, rascunhos, destaques, valorMedio, tipos, bairros };
  }, [items]);

  const recent = items.slice(0, 6);
  const greet = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Bom dia';
    if (h < 18) return 'Boa tarde';
    return 'Boa noite';
  })();

  const goClassic = () => {
    localStorage.setItem('mf_admin_classic', '1');
    if (onBackToClassic) onBackToClassic();
    else window.location.reload();
  };

  return (
    <ThemeProvider theme={theme}>
      <Shell>
        <Sidebar>
          <Brand>
            <img
              src={mode === 'dark' ? '/mf-real-state/logos/mf-logo-dark.png' : '/mf-real-state/logos/mf-logo-light.png'}
              alt="MF Imóveis"
            />
            <span className="tagline">real estate</span>
          </Brand>

          <NavGroup>Painel</NavGroup>
          <NavItem $active={nav === 'dashboard'} onClick={() => setNavSaved('dashboard')}>Visão geral</NavItem>
          <NavItem $active={nav === 'imoveis'} onClick={() => setNavSaved('imoveis')}>Imóveis</NavItem>
          <NavItem $active={nav === 'amenidades'} onClick={() => setNavSaved('amenidades')} style={{ paddingLeft: 28, fontSize: 13 }}>↳ Amenidades</NavItem>
          <NavItem $active={nav === 'corretores'} onClick={() => setNavSaved('corretores')}>Corretores</NavItem>
          <NavItem $active={nav === 'vinculos'} onClick={() => setNavSaved('vinculos')} style={{ paddingLeft: 28, fontSize: 13 }}>↳ Vínculos</NavItem>
          <NavItem disabled>Leads <small>em breve</small></NavItem>
          <NavItem disabled>Visitas <small>em breve</small></NavItem>
          <NavItem onClick={onBlog}>Blog</NavItem>

          {canSeeDisparos && (<>
            <NavGroup>Marketing</NavGroup>
            <NavItem $active={nav === 'disparos'} onClick={() => setNavSaved('disparos')}>
              Disparos
            </NavItem>
          </>)}

          <NavGroup>Sistema</NavGroup>
          <NavItem onClick={onConfig}>Configurações</NavItem>
          <NavItem onClick={onLogout}>Sair</NavItem>
        </Sidebar>

        <Main>
          <Topbar>
            <Crumb>Painel · <span>{nav === 'imoveis' ? 'Imóveis' : nav === 'amenidades' ? 'Amenidades' : nav === 'corretores' ? 'Corretores' : nav === 'vinculos' ? 'Vínculos' : nav === 'disparos' ? 'Disparos' : 'Visão geral'}</span></Crumb>
            <TopActions>
              <ThemeToggle
                onClick={toggleMode}
                title={mode === 'dark' ? 'Modo claro' : 'Modo escuro'}
                aria-label="Alternar tema"
              >
                {mode === 'dark' ? '☀' : '☾'}
              </ThemeToggle>
              <TopBtn $primary onClick={onNew}>+ Novo imóvel</TopBtn>
            </TopActions>
          </Topbar>

          <Content>
          {nav === 'dashboard' && <BiancaDashboard theme={theme} />}

          {nav === 'amenidades' && <AmenidadesPanelV2 items={items} onRefresh={fetchItems} theme={theme} />}

          {nav === 'corretores' && <CorretoresPanelV2 theme={theme} />}
          {nav === 'vinculos' && <VinculosPanelV2 theme={theme} admin={admin} />}

          {nav === 'disparos' && canSeeDisparos && <EmailBlastPanel theme={theme} admin={admin} />}
          {nav === 'disparos' && !canSeeDisparos && <Empty>Acesso restrito.</Empty>}

          {nav === 'imoveis' && (<>
            <PageHead>
              <div>
                <h1>Imóveis</h1>
                <p>{filtered.length} {filtered.length === 1 ? 'imóvel' : 'imóveis'} {filtered.length !== items.length && `de ${items.length}`}</p>
              </div>
              <ViewToggle role="tablist" aria-label="Modo de exibição">
                <ViewToggleBtn $active={display === 'cards'} data-active={display === 'cards'} onClick={() => setDisplaySaved('cards')}>Cards</ViewToggleBtn>
                <ViewToggleBtn $active={display === 'list'} data-active={display === 'list'} onClick={() => setDisplaySaved('list')}>Lista</ViewToggleBtn>
              </ViewToggle>
            </PageHead>

            <Filters>
              <Field>
                Buscar
                <input
                  type="search"
                  placeholder="Título, endereço, condomínio…"
                  value={filters.texto}
                  onChange={setFilter('texto')}
                />
              </Field>
              <Field>
                Status
                <select value={filters.status} onChange={setFilter('status')}>
                  <option value="">Todos</option>
                  <option value="publicado">Publicado</option>
                  <option value="rascunho">Rascunho</option>
                  <option value="vendido">Vendido</option>
                  <option value="inativo">Inativo</option>
                </select>
              </Field>
              <Field>
                Aprovação
                <select value={filters.aprovacao} onChange={setFilter('aprovacao')}>
                  <option value="">Todos</option>
                  <option value="pendente">Pendentes</option>
                  <option value="aprovado">Aprovados</option>
                  <option value="rejeitado">Rejeitados</option>
                </select>
              </Field>
              <Field>
                Tipo
                <select value={filters.tipo} onChange={setFilter('tipo')}>
                  <option value="">Todos</option>
                  <option value="apartamento">Apartamento</option>
                  <option value="casa">Casa</option>
                  <option value="terreno">Terreno</option>
                  <option value="cobertura">Cobertura</option>
                  <option value="comercial">Comercial</option>
                </select>
              </Field>
              <Field>
                Bairro
                <select value={filters.bairro} onChange={setFilter('bairro')}>
                  <option value="">Todos</option>
                  {bairrosUnicos.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </Field>
              <Field>
                Cidade
                <select value={filters.cidade} onChange={setFilter('cidade')}>
                  <option value="">Todas</option>
                  {cidadesUnicas.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field>
                Preço mín
                <input type="number" placeholder="R$" value={filters.precoMin} onChange={setFilter('precoMin')} />
              </Field>
              <Field>
                Preço máx
                <input type="number" placeholder="R$" value={filters.precoMax} onChange={setFilter('precoMax')} />
              </Field>
              <ClearBtn onClick={clearFilters}>Limpar</ClearBtn>
            </Filters>

            <ResultBar>
              <span>Página {safePage} de {totalPages}</span>
              <span>{PAGE_SIZE} por página</span>
            </ResultBar>

            {loading ? (
              <Empty>Carregando…</Empty>
            ) : filtered.length === 0 ? (
              <Empty>Nenhum imóvel encontrado com esses filtros.</Empty>
            ) : display === 'list' ? (
              <>
                <ListWrap>
                  {pageItems.map(p => {
                    const cover = pickCover(p.imagens);
                    return (
                      <ListRow key={p.id}>
                        <ListThumb $star={p.destaque}>
                          {cover && <img src={cover} alt={p.titulo || 'Imóvel'} loading="lazy" />}
                        </ListThumb>
                        <ListInfo>
                          <div
                            className="t"
                            onClick={() => onEdit(p)}
                            style={{ cursor: 'pointer' }}
                            title="Editar este imóvel"
                          >{p.titulo || 'Sem título'}</div>
                          <div className="m" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span>{[p.bairro, p.cidade].filter(Boolean).join(' · ') || '—'} · <span style={{ textTransform: 'capitalize' }}>{p.status}</span></span>
                            {p.status_aprovacao === 'pendente' && <AprovBadge $status="pendente">Pendente</AprovBadge>}
                            {p.status_aprovacao === 'rejeitado' && <AprovBadge $status="rejeitado">Rejeitado</AprovBadge>}
                          </div>
                          <div className="s">
                            {p.area_m2 && <span><IconArea size={12} />{p.area_m2} m²</span>}
                            {p.quartos && <span><IconBed size={12} />{p.quartos} {p.quartos == 1 ? 'quarto' : 'quartos'}</span>}
                            {p.suites && <span><IconShower size={12} />{p.suites} {p.suites == 1 ? 'suíte' : 'suítes'}</span>}
                            {p.vagas && <span><IconCar size={12} />{p.vagas} {p.vagas == 1 ? 'vaga' : 'vagas'}</span>}
                          </div>
                        </ListInfo>
                        <ListPrice>{p.preco ? formatPrice(p.preco) : '—'}</ListPrice>
                        <ListActions>
                          {p.status_aprovacao === 'pendente' && (
                            <>
                              <button onClick={() => handleAprovar(p)} style={{ color: '#2E7D5B', fontWeight: 600 }}>Aprovar</button>
                              <button onClick={() => handleRejeitar(p)} style={{ color: '#C04848', fontWeight: 600 }}>Rejeitar</button>
                            </>
                          )}
                          <a
                            href={`${MF_BASE}/imoveis/${buildPropertySlug(p)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Abrir no site (nova aba)"
                            style={{ textDecoration: 'none' }}
                          >
                            <button type="button">Ver ↗</button>
                          </a>
                          <button onClick={() => onEdit(p)}>Editar</button>
                          {p.status_aprovacao !== 'pendente' && (
                            <button onClick={() => handleToggleStatus(p)}>
                              {p.status === 'publicado' ? 'Despublicar' : 'Publicar'}
                            </button>
                          )}
                          <button onClick={() => handleToggleDestaque(p)} title="Destaque" style={{ color: p.destaque ? '#C9A06A' : undefined }}>★</button>
                          <button data-danger="true" onClick={() => handleDelete(p.id)}>Excluir</button>
                        </ListActions>
                      </ListRow>
                    );
                  })}
                </ListWrap>

                {totalPages > 1 && (
                  <Pager>
                    <button disabled={safePage === 1} onClick={() => setPage(1)}>«</button>
                    <button disabled={safePage === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹</button>
                    {pageNumbers(safePage, totalPages).map((n, i) =>
                      n === '…' ? (
                        <span key={'e' + i} style={{ padding: '0 6px', opacity: .5 }}>…</span>
                      ) : (
                        <button key={n} data-active={n === safePage} onClick={() => setPage(n)}>{n}</button>
                      )
                    )}
                    <button disabled={safePage === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>›</button>
                    <button disabled={safePage === totalPages} onClick={() => setPage(totalPages)}>»</button>
                  </Pager>
                )}
              </>
            ) : (
              <>
                <Grid>
                  {pageItems.map(p => {
                    const cover = pickCover(p.imagens);
                    return (
                      <Card key={p.id}>
                        <CardCover>
                          {cover ? <img src={cover} alt={p.titulo || 'Imóvel'} loading="lazy" /> : <span className="ph">sem foto</span>}
                          <CoverBadges>
                            {p.status === 'publicado' && <Badge>Publicado</Badge>}
                            {p.status === 'rascunho' && <Badge>Rascunho</Badge>}
                            {p.status === 'vendido' && <Badge>Vendido</Badge>}
                            {p.status === 'inativo' && <Badge>Inativo</Badge>}
                            {p.status_aprovacao === 'pendente' && <AprovBadge $status="pendente">Pendente</AprovBadge>}
                            {p.status_aprovacao === 'rejeitado' && <AprovBadge $status="rejeitado">Rejeitado</AprovBadge>}
                          </CoverBadges>
                          <CoverActions>
                            <CircleBtn
                              as="a"
                              href={`${MF_BASE}/imoveis/${buildPropertySlug(p)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              title="Abrir no site (nova aba)"
                              aria-label="Abrir no site"
                            >↗</CircleBtn>
                            <CircleBtn
                              $on={p.destaque}
                              onClick={() => handleToggleDestaque(p)}
                              title={p.destaque ? 'Remover destaque' : 'Marcar como destaque'}
                              aria-label="Destaque"
                            >★</CircleBtn>
                          </CoverActions>
                        </CardCover>
                        <CardBody>
                          <CardTitle
                            onClick={() => onEdit(p)}
                            style={{ cursor: 'pointer' }}
                            title="Editar este imóvel"
                          >
                            {p.titulo || 'Sem título'}
                          </CardTitle>
                          <CardLoc>{[p.bairro, p.cidade].filter(Boolean).join(' · ') || '—'}</CardLoc>
                          <CardSpecs>
                            {p.area_m2 && <span><IconArea />{p.area_m2} m²</span>}
                            {p.quartos && <span><IconBed />{p.quartos} {p.quartos == 1 ? 'quarto' : 'quartos'}</span>}
                            {p.suites && <span><IconShower />{p.suites} {p.suites == 1 ? 'suíte' : 'suítes'}</span>}
                            {p.vagas && <span><IconCar />{p.vagas} {p.vagas == 1 ? 'vaga' : 'vagas'}</span>}
                          </CardSpecs>
                          <CardPrice>{p.preco ? formatPrice(p.preco) : '—'}</CardPrice>
                        </CardBody>
                        <CardActions>
                          {p.status_aprovacao === 'pendente' && (
                            <>
                              <button onClick={() => handleAprovar(p)} style={{ color: '#2E7D5B' }}>Aprovar</button>
                              <button onClick={() => handleRejeitar(p)} style={{ color: '#C04848' }}>Rejeitar</button>
                            </>
                          )}
                          <button onClick={() => onEdit(p)}>Editar</button>
                          {p.status_aprovacao !== 'pendente' && (
                            <button onClick={() => handleToggleStatus(p)}>
                              {p.status === 'publicado' ? 'Despublicar' : 'Publicar'}
                            </button>
                          )}
                          <button data-danger="true" onClick={() => handleDelete(p.id)}>Excluir</button>
                        </CardActions>
                      </Card>
                    );
                  })}
                </Grid>

                {totalPages > 1 && (
                  <Pager>
                    <button disabled={safePage === 1} onClick={() => setPage(1)}>«</button>
                    <button disabled={safePage === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹</button>
                    {pageNumbers(safePage, totalPages).map((n, i) =>
                      n === '…' ? (
                        <span key={'e' + i} style={{ padding: '0 6px', color: 'inherit', opacity: .5 }}>…</span>
                      ) : (
                        <button
                          key={n}
                          data-active={n === safePage}
                          onClick={() => setPage(n)}
                        >{n}</button>
                      )
                    )}
                    <button disabled={safePage === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>›</button>
                    <button disabled={safePage === totalPages} onClick={() => setPage(totalPages)}>»</button>
                  </Pager>
                )}
              </>
            )}
          </>)}
          </Content>
        </Main>
      </Shell>
    </ThemeProvider>
  );
}

function pageNumbers(current, total) {
  const out = [];
  const push = (n) => out.push(n);
  if (total <= 7) {
    for (let i = 1; i <= total; i++) push(i);
    return out;
  }
  push(1);
  if (current > 3) push('…');
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) push(i);
  if (current < total - 2) push('…');
  push(total);
  return out;
}
